// PTZ Controls Component
// Pan-Tilt-Zoom controls and preset management for video cameras

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Home,
  ZoomIn,
  ZoomOut,
  Plus,
  Trash2,
  Save,
  List,
  X,
  Loader2,
  Move,
} from 'lucide-react';
import { ptzApi, PTZCapabilities, PTZPreset } from '../../services/api';

import { useTranslation } from 'react-i18next';
interface PTZControlsProps {
  deviceId: string;
  onClose?: () => void;
  compact?: boolean; // Compact mode for overlay
}

export function PTZControls({ deviceId, onClose, compact = false }: PTZControlsProps) {
  const { t } = useTranslation('cameras');
  const [capabilities, setCapabilities] = useState<PTZCapabilities | null>(null);
  const [presets, setPresets] = useState<PTZPreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [moving, setMoving] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track active movement for continuous PTZ
  const moveIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load PTZ capabilities and presets
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [capsResult, presetsResult] = await Promise.all([
          ptzApi.getCapabilities(deviceId),
          ptzApi.getPresets(deviceId),
        ]);
        setCapabilities(capsResult.capabilities);
        setPresets(presetsResult.presets || []);
      } catch (err) {
        console.error('[PTZ] Failed to load capabilities:', err);
        setError('Failed to load PTZ capabilities');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [deviceId]);

  // Start continuous movement
  const startMove = useCallback(async (pan: number, tilt: number, zoom: number) => {
    if (!capabilities) return;

    setMoving(true);
    try {
      await ptzApi.move(deviceId, { pan, tilt, zoom });
    } catch (err) {
      console.error('[PTZ] Move failed:', err);
    }
  }, [deviceId, capabilities]);

  // Stop movement
  const stopMove = useCallback(async () => {
    if (moveIntervalRef.current) {
      clearInterval(moveIntervalRef.current);
      moveIntervalRef.current = null;
    }

    try {
      await ptzApi.stop(deviceId);
    } catch (err) {
      console.error('[PTZ] Stop failed:', err);
    } finally {
      setMoving(false);
    }
  }, [deviceId]);

  // Go to home position
  const goHome = useCallback(async () => {
    try {
      await ptzApi.home(deviceId);
    } catch (err) {
      console.error('[PTZ] Home failed:', err);
    }
  }, [deviceId]);

  // Go to preset
  const gotoPreset = useCallback(async (presetId: string) => {
    try {
      await ptzApi.gotoPreset(deviceId, presetId);
      setShowPresets(false);
    } catch (err) {
      console.error('[PTZ] Goto preset failed:', err);
    }
  }, [deviceId]);

  // Save new preset
  const savePreset = useCallback(async () => {
    if (!newPresetName.trim()) return;

    setSavingPreset(true);
    try {
      const result = await ptzApi.createPreset(deviceId, newPresetName.trim());
      setPresets(prev => [...prev, result.preset]);
      setNewPresetName('');
    } catch (err) {
      console.error('[PTZ] Save preset failed:', err);
    } finally {
      setSavingPreset(false);
    }
  }, [deviceId, newPresetName]);

  // Delete preset
  const deletePreset = useCallback(async (presetId: string) => {
    try {
      await ptzApi.deletePreset(deviceId, presetId);
      setPresets(prev => prev.filter(p => p.id !== presetId));
    } catch (err) {
      console.error('[PTZ] Delete preset failed:', err);
    }
  }, [deviceId]);

  // Keyboard shortcuts: 1-8 for presets when PTZ is visible
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const num = parseInt(e.key);
      if (num >= 1 && num <= 8 && presets.length >= num) {
        e.preventDefault();
        gotoPreset(presets[num - 1].id);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [presets, gotoPreset]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (moveIntervalRef.current) {
        clearInterval(moveIntervalRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${compact ? 'p-2' : 'p-4'}`}>
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !capabilities) {
    return (
      <div className={`text-gray-400 text-sm ${compact ? 'p-2' : 'p-4'}`}>
        {error || 'PTZ not available for this camera'}
      </div>
    );
  }

  const hasPanTilt = capabilities.pan || capabilities.tilt;
  const hasZoom = capabilities.zoom;
  const hasPresets = capabilities.presets;
  const hasHome = capabilities.home;

  // Compact overlay mode
  if (compact) {
    return (
      <div className="flex items-center gap-1 bg-black/70 rounded-lg p-1">
        {/* D-pad for pan/tilt */}
        {hasPanTilt && (
          <div className="flex items-center gap-0.5">
            <button
              className="p-1 hover:bg-white/20 rounded"
              onMouseDown={() => startMove(-50, 0, 0)}
              onMouseUp={stopMove}
              onMouseLeave={stopMove}
              title={t('panLeft')}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex flex-col gap-0.5">
              <button
                className="p-1 hover:bg-white/20 rounded"
                onMouseDown={() => startMove(0, 50, 0)}
                onMouseUp={stopMove}
                onMouseLeave={stopMove}
                title={t('tiltUp')}
              >
                <ChevronUp className="w-4 h-4" />
              </button>
              <button
                className="p-1 hover:bg-white/20 rounded"
                onMouseDown={() => startMove(0, -50, 0)}
                onMouseUp={stopMove}
                onMouseLeave={stopMove}
                title={t('tiltDown')}
              >
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
            <button
              className="p-1 hover:bg-white/20 rounded"
              onMouseDown={() => startMove(50, 0, 0)}
              onMouseUp={stopMove}
              onMouseLeave={stopMove}
              title={t('panRight')}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Zoom */}
        {hasZoom && (
          <div className="flex items-center gap-0.5 border-s border-white/20 ps-1 ms-1">
            <button
              className="p-1 hover:bg-white/20 rounded"
              onMouseDown={() => startMove(0, 0, 50)}
              onMouseUp={stopMove}
              onMouseLeave={stopMove}
              title={t('zoomIn_e59a')}
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button
              className="p-1 hover:bg-white/20 rounded"
              onMouseDown={() => startMove(0, 0, -50)}
              onMouseUp={stopMove}
              onMouseLeave={stopMove}
              title={t('zoomOut_87af')}
            >
              <ZoomOut className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Home */}
        {hasHome && (
          <button
            className="p-1 hover:bg-white/20 rounded border-s border-white/20 ms-1 ps-1"
            onClick={goHome}
            title={t('goHome')}
          >
            <Home className="w-4 h-4" />
          </button>
        )}

        {/* Preset quick-access buttons (1-8) */}
        {hasPresets && presets.length > 0 && (
          <div className="flex items-center gap-0.5 border-s border-white/20 ms-1 ps-1">
            {presets.slice(0, 8).map((preset, idx) => (
              <button
                key={preset.id}
                className="w-5 h-5 flex items-center justify-center text-[10px] font-bold bg-gray-700/80 hover:bg-blue-600 rounded transition-colors"
                onClick={() => gotoPreset(preset.id)}
                title={`Preset ${idx + 1}: ${preset.name}`}
              >
                {idx + 1}
              </button>
            ))}
          </div>
        )}

        {/* Presets toggle (full list + save) */}
        {hasPresets && (
          <button
            className={`p-1 hover:bg-white/20 rounded ${!presets.length ? 'border-s border-white/20 ms-1 ps-1' : 'ms-0.5'} ${showPresets ? 'bg-white/20' : ''}`}
            onClick={() => setShowPresets(!showPresets)}
            title={t('managePresets')}
          >
            <List className="w-4 h-4" />
          </button>
        )}

        {/* Presets dropdown */}
        {showPresets && hasPresets && (
          <div className="absolute top-full left-0 mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-lg p-2 min-w-[150px] z-50">
            <div className="text-xs text-gray-400 mb-1">{t('presets_e709')}</div>
            {presets.length === 0 ? (
              <div className="text-xs text-gray-500">{t('noPresets')}</div>
            ) : (
              <div className="space-y-1 max-h-[200px] overflow-y-auto">
                {presets.map(preset => (
                  <div key={preset.id} className="flex items-center gap-1">
                    <button
                      className="flex-1 text-start text-xs px-2 py-1 hover:bg-gray-800 rounded truncate"
                      onClick={() => gotoPreset(preset.id)}
                    >
                      {preset.name}
                    </button>
                    <button
                      className="p-1 hover:bg-red-900/50 rounded text-red-400"
                      onClick={() => deletePreset(preset.id)}
                      title={t('delete')}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-2 pt-2 border-t border-gray-700 flex gap-1">
              <input
                type="text"
                placeholder={t('newPresetName')}
                value={newPresetName}
                onChange={e => setNewPresetName(e.target.value)}
                className="flex-1 text-xs bg-gray-800 border border-gray-600 rounded px-2 py-1"
                onKeyDown={e => e.key === 'Enter' && savePreset()}
              />
              <button
                className="p-1 hover:bg-blue-900/50 rounded text-blue-400 disabled:opacity-50"
                onClick={savePreset}
                disabled={!newPresetName.trim() || savingPreset}
                title={t('saveCurrentPosition')}
              >
                {savingPreset ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Full panel mode
  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium flex items-center gap-2">
          <Move className="w-4 h-4" />
          {t('ptzControl')}
        </h3>
        {onClose && (
          <button onClick={onClose} className="p-1 hover:bg-gray-800 rounded">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="flex gap-4">
        {/* Pan/Tilt D-pad */}
        {hasPanTilt && (
          <div className="flex flex-col items-center">
            <span className="text-xs text-gray-400 mb-2">{t('panTilt')}</span>
            <div className="grid grid-cols-3 gap-1">
              <div />
              <button
                className="p-2 bg-gray-800 hover:bg-gray-700 rounded-t-lg transition-colors"
                onMouseDown={() => startMove(0, 50, 0)}
                onMouseUp={stopMove}
                onMouseLeave={stopMove}
              >
                <ChevronUp className="w-5 h-5" />
              </button>
              <div />
              <button
                className="p-2 bg-gray-800 hover:bg-gray-700 rounded-s-lg transition-colors"
                onMouseDown={() => startMove(-50, 0, 0)}
                onMouseUp={stopMove}
                onMouseLeave={stopMove}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              {hasHome ? (
                <button
                  className="p-2 bg-gray-800 hover:bg-blue-700 rounded transition-colors"
                  onClick={goHome}
                  title={t('goToHomePosition')}
                >
                  <Home className="w-5 h-5" />
                </button>
              ) : (
                <div className="p-2 bg-gray-900 rounded" />
              )}
              <button
                className="p-2 bg-gray-800 hover:bg-gray-700 rounded-e-lg transition-colors"
                onMouseDown={() => startMove(50, 0, 0)}
                onMouseUp={stopMove}
                onMouseLeave={stopMove}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <div />
              <button
                className="p-2 bg-gray-800 hover:bg-gray-700 rounded-b-lg transition-colors"
                onMouseDown={() => startMove(0, -50, 0)}
                onMouseUp={stopMove}
                onMouseLeave={stopMove}
              >
                <ChevronDown className="w-5 h-5" />
              </button>
              <div />
            </div>
          </div>
        )}

        {/* Zoom */}
        {hasZoom && (
          <div className="flex flex-col items-center">
            <span className="text-xs text-gray-400 mb-2">{t('zoom')}</span>
            <div className="flex flex-col gap-1">
              <button
                className="p-3 bg-gray-800 hover:bg-gray-700 rounded-t-lg transition-colors"
                onMouseDown={() => startMove(0, 0, 50)}
                onMouseUp={stopMove}
                onMouseLeave={stopMove}
              >
                <ZoomIn className="w-5 h-5" />
              </button>
              <button
                className="p-3 bg-gray-800 hover:bg-gray-700 rounded-b-lg transition-colors"
                onMouseDown={() => startMove(0, 0, -50)}
                onMouseUp={stopMove}
                onMouseLeave={stopMove}
              >
                <ZoomOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Presets */}
      {hasPresets && (
        <div className="border-t border-gray-700 pt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-400">{t('presets_e709')}</span>
            <span className="text-xs text-gray-500">{presets.length} {t('saved')}</span>
          </div>

          {/* Preset list */}
          <div className="space-y-1 max-h-[150px] overflow-y-auto mb-3">
            {presets.length === 0 ? (
              <div className="text-sm text-gray-500 text-center py-2">
                {t('noPresetsSaved')}
              </div>
            ) : (
              presets.map(preset => (
                <div
                  key={preset.id}
                  className="flex items-center gap-2 bg-gray-800 rounded px-2 py-1.5 group"
                >
                  <button
                    className="flex-1 text-start text-sm hover:text-blue-400 transition-colors truncate"
                    onClick={() => gotoPreset(preset.id)}
                  >
                    {preset.name}
                  </button>
                  <button
                    className="p-1 opacity-0 group-hover:opacity-100 hover:bg-red-900/50 rounded text-red-400 transition-opacity"
                    onClick={() => deletePreset(preset.id)}
                    title={t('deletePreset')}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Save new preset */}
          <div className="flex gap-2">
            <input
              type="text"
              placeholder={t('newPresetName')}
              value={newPresetName}
              onChange={e => setNewPresetName(e.target.value)}
              className="flex-1 text-sm bg-gray-800 border border-gray-600 rounded px-3 py-1.5 focus:outline-none focus:border-blue-500"
              onKeyDown={e => e.key === 'Enter' && savePreset()}
            />
            <button
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 rounded text-sm transition-colors flex items-center gap-1"
              onClick={savePreset}
              disabled={!newPresetName.trim() || savingPreset}
            >
              {savingPreset ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  {t('save')}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {moving && (
        <div className="text-xs text-blue-400 text-center animate-pulse">
          {t('moving')}
        </div>
      )}
    </div>
  );
}

export default PTZControls;
